import React, { useState, useEffect, useRef } from 'react';
import AgoraRTC from 'agora-rtc-sdk-ng';
import styled, { keyframes, css } from 'styled-components';
import { FaMicrophone, FaMicrophoneSlash, FaVideo, FaVideoSlash, FaPhoneSlash, FaCog, FaCircle, FaStop } from 'react-icons/fa';

const VideoCallContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-height: 100%;
  background: linear-gradient(135deg, #0f172a 0%, #1e1b4b 50%, #0f172a 100%);
  color: white;
  font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 20px;
  flex-shrink: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid rgba(99, 102, 241, 0.3);
`;

const CallInfo = styled.div`
  text-align: center;
  flex: 1;
`;

const CallTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 500;
  color: white;
`;

const CallStatus = styled.p`
  margin: 4px 0 0 0;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
`;

const MainContent = styled.div`
  display: flex;
  flex: 1;
  gap: 20px;
  padding: 20px;
  overflow: hidden;
`;

const LeftSection = styled.div`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%);
  border: 1px solid rgba(99, 102, 241, 0.2);
  border-radius: 12px;
  padding: 40px;
`;

const WaitingMessage = styled.div`
  text-align: center;
  color: rgba(165, 180, 252, 0.9);
  font-size: 16px;
`;

const VideoSection = styled.div`
  flex: 1.2;
  display: flex;
  flex-direction: column;
`;

const VideoContainer = styled.div`
  position: relative;
  background: linear-gradient(135deg, rgba(30, 27, 75, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%);
  border: 1px solid rgba(99, 102, 241, 0.3);
  border-radius: 12px;
  overflow: hidden;
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.15);
`;

const SplitViewContainer = styled.div`
  display: flex;
  gap: 16px;
  flex: 1;
  padding: 16px;
  min-height: 0;
  height: calc(100vh - 220px);
  max-height: calc(100vh - 220px);
  overflow: hidden;
`;

const FullScreenContainer = styled.div`
  position: relative;
  height: 100%;
  width: 100%;
`;

const PictureInPictureContainer = styled.div`
  position: relative;
  flex: 1;
  padding: 16px;
  min-height: 0;
  height: calc(100vh - 220px);
  max-height: calc(100vh - 220px);
  overflow: hidden;
`;

const SmallVideoContainer = styled.div`
  position: absolute;
  top: 20px;
  right: 20px;
  width: 200px;
  height: 150px;
  background: linear-gradient(135deg, rgba(30, 27, 75, 0.9) 0%, rgba(15, 23, 42, 0.95) 100%);
  border-radius: 12px;
  overflow: hidden;
  border: 2px solid rgba(99, 102, 241, 0.5);
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.25);
  z-index: 10;
`;

const ViewModeControls = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

const ViewModeButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 1px solid ${props => props.$active ? 'rgba(99, 102, 241, 0.7)' : 'rgba(99, 102, 241, 0.3)'};
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, rgba(99, 102, 241, 0.3) 0%, rgba(79, 70, 229, 0.4) 100%)'
    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(79, 70, 229, 0.15) 100%)'};
  color: ${props => props.$active ? '#a5b4fc' : 'rgba(165, 180, 252, 0.7)'};
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: ${props => props.$active ? '0 4px 12px rgba(99, 102, 241, 0.25)' : 'none'};

  &:hover {
    transform: scale(1.05);
    border-color: ${props => props.$active ? 'rgba(99, 102, 241, 0.9)' : 'rgba(99, 102, 241, 0.5)'};
    color: ${props => props.$active ? '#c7d2fe' : '#a5b4fc'};
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
`;

const VideoElement = styled.video`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 12px;
`;

const VideoLabel = styled.div`
  position: absolute;
  bottom: 12px;
  left: 12px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.8) 0%, rgba(99, 102, 241, 0.9) 100%);
  backdrop-filter: blur(8px);
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  color: white;
  border: 1px solid rgba(165, 180, 252, 0.3);
`;

const Controls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 16px;
  padding: 24px 20px;
  flex-shrink: 0;
  height: 100px;
  min-height: 100px;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(147, 51, 234, 0.15) 100%);
  backdrop-filter: blur(12px);
  border-top: 1px solid rgba(99, 102, 241, 0.3);
  z-index: 100;
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 2px solid ${props => {
    if (props.danger) return 'rgba(239, 68, 68, 0.5)';
    if (props.active) return 'rgba(16, 185, 129, 0.5)';
    return 'rgba(99, 102, 241, 0.4)';
  }};
  background: ${props => {
    if (props.danger) return 'linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(185, 28, 28, 0.3) 100%)';
    if (props.active) return 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(5, 150, 105, 0.3) 100%)';
    return 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.3) 100%)';
  }};
  color: ${props => {
    if (props.danger) return '#ef4444';
    if (props.active) return '#10b981';
    return '#a5b4fc';
  }};
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: ${props => {
    if (props.danger) return '0 8px 32px rgba(239, 68, 68, 0.25)';
    if (props.active) return '0 8px 32px rgba(16, 185, 129, 0.25)';
    return '0 8px 32px rgba(99, 102, 241, 0.2)';
  }};

  &:hover {
    transform: scale(1.05);
    border-color: ${props => {
      if (props.danger) return 'rgba(239, 68, 68, 0.7)';
      if (props.active) return 'rgba(16, 185, 129, 0.7)';
      return 'rgba(99, 102, 241, 0.6)';
    }};
    box-shadow: ${props => {
      if (props.danger) return '0 12px 40px rgba(239, 68, 68, 0.4)';
      if (props.active) return '0 12px 40px rgba(16, 185, 129, 0.4)';
      return '0 12px 40px rgba(99, 102, 241, 0.35)';
    }};
    color: ${props => {
      if (props.danger) return '#f87171';
      if (props.active) return '#34d399';
      return '#c7d2fe';
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const SettingsButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 6px;
  border: 2px solid rgba(99, 102, 241, 0.4);
  background: linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.3) 100%);
  color: #a5b4fc;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.2);

  &:hover {
    border-color: rgba(99, 102, 241, 0.6);
    color: #c7d2fe;
    transform: scale(1.05);
    box-shadow: 0 12px 40px rgba(99, 102, 241, 0.35);
  }
`;

const LoadingSpinner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 16px;
  color: rgba(165, 180, 252, 0.9);
`;

// Recording indicator animation
const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.1);
    opacity: 0.7;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const RecordingIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  background: ${props => props.$isRecording 
    ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(185, 28, 28, 0.4) 100%)'
    : 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.3) 100%)'};
  border: 1px solid ${props => props.$isRecording 
    ? 'rgba(239, 68, 68, 0.5)' 
    : 'rgba(99, 102, 241, 0.3)'};
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.$isRecording ? '#ef4444' : '#a5b4fc'};
`;

const RecordingDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #ef4444;
  animation: ${pulseAnimation} 1.5s ease-in-out infinite;
`;

const RecordingButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  border-radius: 12px;
  border: 2px solid ${props => {
    if (props.$isRecording) return 'rgba(239, 68, 68, 0.6)';
    if (props.disabled) return 'rgba(99, 102, 241, 0.2)';
    return 'rgba(239, 68, 68, 0.4)';
  }};
  background: ${props => {
    if (props.$isRecording) return 'linear-gradient(135deg, rgba(239, 68, 68, 0.3) 0%, rgba(185, 28, 28, 0.4) 100%)';
    return 'linear-gradient(135deg, rgba(99, 102, 241, 0.2) 0%, rgba(79, 70, 229, 0.3) 100%)';
  }};
  color: ${props => {
    if (props.$isRecording) return '#ef4444';
    if (props.disabled) return 'rgba(165, 180, 252, 0.4)';
    return '#ef4444';
  }};
  font-size: 18px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  box-shadow: ${props => props.$isRecording 
    ? '0 8px 32px rgba(239, 68, 68, 0.3)' 
    : '0 8px 32px rgba(99, 102, 241, 0.2)'};
  position: relative;

  ${props => props.$isRecording && css`
    animation: ${pulseAnimation} 2s ease-in-out infinite;
  `}

  &:hover:not(:disabled) {
    transform: scale(1.05);
    border-color: rgba(239, 68, 68, 0.7);
    box-shadow: 0 12px 40px rgba(239, 68, 68, 0.4);
  }

  &:disabled {
    opacity: 0.5;
    transform: none;
  }
`;

const ErrorMessage = styled.div`
  background-color: rgba(229, 62, 62, 0.1);
  border: 1px solid rgba(229, 62, 62, 0.3);
  color: #FEB2B2;
  padding: 16px;
  border-radius: 8px;
  margin: 20px;
  text-align: center;
  
  button {
    margin-top: 10px;
    padding: 8px 16px;
    background-color: #E53E3E;
    color: white;
    border: none;
    border-radius: 4px;
    cursor: pointer;
    
    &:hover {
      background-color: #C53030;
    }
  }
`;

const VideoCall = ({ 
  appId, 
  channelName, 
  token, 
  uid, 
  onCallEnd, 
  userName = "User",
  onError,
  threadId,
  authenticatedAxios // For API calls
}) => {
  const [client, setClient] = useState(null);
  const [localAudioTrack, setLocalAudioTrack] = useState(null);
  const [localVideoTrack, setLocalVideoTrack] = useState(null);
  const [remoteUsers, setRemoteUsers] = useState([]);
  const [isAudioEnabled, setIsAudioEnabled] = useState(true);
  const [isVideoEnabled, setIsVideoEnabled] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [callDuration, setCallDuration] = useState(0);
  const [isTestMode, setIsTestMode] = useState(false);
  const [viewMode, setViewMode] = useState('split'); // 'split' or 'pip'
  
  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [isStoppingRecording, setIsStoppingRecording] = useState(false); // Immediately true when stop is clicked
  const [recordingId, setRecordingId] = useState(null);
  const [recordingStatus, setRecordingStatus] = useState('idle'); // idle, starting, recording, stopping, processing, done, failed
  const [recordingError, setRecordingError] = useState(null);

  const localVideoRef = useRef(null);
  const remoteVideoRefs = useRef({});
  const callTimerRef = useRef(null);

  // Test mode support
  useEffect(() => {
    if (appId === 'test-app-id' && token === 'test-token') {
      setIsTestMode(true);
      setIsConnected(true);
      setCallDuration(0);
      callTimerRef.current = setInterval(() => {
        setCallDuration(prev => prev + 1);
      }, 1000);
    }
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
        callTimerRef.current = null;
      }
    };
  }, [appId, token]);

  // Initialize Agora client
  useEffect(() => {
    if (isTestMode) return;
    const initClient = async () => {
      try {
        if (!AgoraRTC) {
          throw new Error('Agora SDK not loaded. Please check if agora-rtc-sdk-ng is installed.');
        }
        const agoraClient = AgoraRTC.createClient({ mode: 'rtc', codec: 'vp8' });
        setClient(agoraClient);
      } catch (err) {
        setError(`Failed to initialize video call client: ${err.message}`);
        if (onError) onError(err);
      }
    };
    initClient();
    return () => {
      if (client) {
        client.removeAllListeners();
      }
    };
  }, [isTestMode]);

  // Handle client events
  useEffect(() => {
    if (!client || isTestMode) return;
    const handleUserPublished = async (user, mediaType) => {
      await client.subscribe(user, mediaType);
      if (mediaType === 'video') {
        setRemoteUsers(prev => {
          const existing = prev.find(u => u.uid === user.uid);
          if (!existing) {
            return [...prev, user];
          }
          return prev;
        });
      }
      if (mediaType === 'audio') {
        user.audioTrack?.play();
      }
    };
    const handleUserUnpublished = (user, mediaType) => {
      console.log(`User ${user.uid} unpublished ${mediaType}`);
      // Only remove user from remoteUsers if they unpublished video
      // Audio unpublish should not remove the user (they're just muted)
      if (mediaType === 'video') {
        setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
      }
    };
    const handleUserLeft = (user) => {
      console.log(`User ${user.uid} left the channel`);
      setRemoteUsers(prev => prev.filter(u => u.uid !== user.uid));
    };
    
    // Handle mute/unmute events (this is crucial for proper mute handling)
    const handleUserMuteUpdated = (user, mediaType, muted) => {
      console.log(`User ${user.uid} ${muted ? 'muted' : 'unmuted'} their ${mediaType}`);
      // This event helps us track mute states without removing users
      // We don't need to do anything special here since the user is still connected
    };
    
    client.on('user-published', handleUserPublished);
    client.on('user-unpublished', handleUserUnpublished);
    client.on('user-left', handleUserLeft);
    client.on('user-mute-updated', handleUserMuteUpdated);
    
    return () => {
      client.off('user-published', handleUserPublished);
      client.off('user-unpublished', handleUserUnpublished);
      client.off('user-left', handleUserLeft);
      client.off('user-mute-updated', handleUserMuteUpdated);
    };
  }, [client, isTestMode]);

  // Connect to channel
  useEffect(() => {
    if (isTestMode) return;
    const connectToChannel = async () => {
      if (!client || !appId || !channelName || !token) return;
      setIsConnecting(true);
      setError(null);
      try {
        // Check for media permissions first
        try {
          await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        } catch (mediaErr) {
          throw new Error(`Camera/microphone access denied: ${mediaErr.message}`);
        }
        await client.join(appId, channelName, token, uid);
        console.log('Successfully joined channel:', channelName, 'with UID:', uid);
        setIsConnected(true);
        setIsConnecting(false);
        const [audioTrack, videoTrack] = await AgoraRTC.createMicrophoneAndCameraTracks();
        
        // Add track event listeners for debugging
        audioTrack.on("track-ended", () => {
          console.warn("Audio track ended unexpectedly");
        });
        
        videoTrack.on("track-ended", () => {
          console.warn("Video track ended unexpectedly");
        });
        
        setLocalAudioTrack(audioTrack);
        setLocalVideoTrack(videoTrack);
        await client.publish([audioTrack, videoTrack]);
        console.log('Successfully published audio and video tracks');
        console.log('Local tracks published:', client.localTracks?.length || 0);
        if (localVideoRef.current) {
          videoTrack.play(localVideoRef.current);
        }
        callTimerRef.current = setInterval(() => {
          setCallDuration(prev => prev + 1);
        }, 1000);
      } catch (err) {
        setError(`Failed to connect to video call: ${err.message}`);
        setIsConnecting(false);
        if (onError) onError(err);
      }
    };
    connectToChannel();
  }, [client, appId, channelName, token, uid, isTestMode]);

  // Handle remote video elements
  useEffect(() => {
    remoteUsers.forEach(user => {
      if (user.videoTrack && remoteVideoRefs.current[user.uid]) {
        user.videoTrack.play(remoteVideoRefs.current[user.uid]);
      }
    });
  }, [remoteUsers, viewMode]);

  // Handle local video playback when view mode changes
  useEffect(() => {
    if (localVideoTrack && localVideoRef.current) {
      localVideoTrack.play(localVideoRef.current);
    }
  }, [localVideoTrack, viewMode]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
  }, []);

  // Cleanup camera/mic on page unload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }
      if (client) {
        client.leave && client.leave();
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [localAudioTrack, localVideoTrack, client]);

  const toggleAudio = async () => {
    if (!localAudioTrack || !client) return;
    
    try {
      if (isAudioEnabled) {
        // Method 1: Try setVolume to 0 (complete silence without unpublishing)
        if (typeof localAudioTrack.setVolume === 'function') {
          localAudioTrack.setVolume(0);
          console.log('Audio muted using setVolume(0) - track stays fully published');
        } else {
          // Method 2: Use setEnabled as fallback
          await localAudioTrack.setEnabled(false);
          console.log('Audio muted using setEnabled - track disabled but published');
        }
        setIsAudioEnabled(false);
      } else {
        // Unmute audio
        if (typeof localAudioTrack.setVolume === 'function') {
          localAudioTrack.setVolume(100);
          console.log('Audio unmuted using setVolume(100)');
        } else {
          await localAudioTrack.setEnabled(true);
          console.log('Audio unmuted using setEnabled');
        }
        setIsAudioEnabled(true);
      }
      
      // Log current track status for debugging
      console.log('Audio track enabled:', localAudioTrack.enabled);
      console.log('Published tracks count:', client.localTracks?.length || 0);
      
    } catch (error) {
      console.error('Error toggling audio:', error);
      setIsAudioEnabled(!isAudioEnabled);
    }
  };

  const toggleVideo = async () => {
    if (!localVideoTrack) return;
    
    try {
      if (isVideoEnabled) {
        // Disable video by disabling the track (keeps it published but shows black frame)
        await localVideoTrack.setEnabled(false);
        setIsVideoEnabled(false);
        console.log('Video disabled - track disabled but still published');
      } else {
        // Enable video by enabling the track
        await localVideoTrack.setEnabled(true);
        setIsVideoEnabled(true);
        console.log('Video enabled - track enabled');
        // Ensure video plays locally after enabling
        if (localVideoRef.current) {
          localVideoTrack.play(localVideoRef.current);
        }
      }
    } catch (error) {
      console.error('Error toggling video:', error);
      // Reset state if operation failed
      setIsVideoEnabled(!isVideoEnabled);
    }
  };

  const endCall = async () => {
    try {
      // Stop recording if active before ending call
      if (isRecording && recordingId) {
        try {
          await stopRecording();
        } catch (err) {
          console.error('Error stopping recording before end call:', err);
        }
      }
      
      if (isTestMode) {
        if (callTimerRef.current) {
          clearInterval(callTimerRef.current);
        }
        setIsConnected(false);
        if (onCallEnd) {
          onCallEnd(callDuration);
        }
        return;
      }
      if (localAudioTrack) {
        localAudioTrack.close();
      }
      if (localVideoTrack) {
        localVideoTrack.close();
      }
      if (client) {
        await client.leave();
      }
      if (callTimerRef.current) {
        clearInterval(callTimerRef.current);
      }
      setIsConnected(false);
      if (onCallEnd) {
        onCallEnd(callDuration);
      }
    } catch (err) {
      console.error('Error ending call:', err);
    }
  };

  // Start recording
  const startRecording = async () => {
    if (!authenticatedAxios || !channelName) {
      console.error('Cannot start recording: missing authenticatedAxios or channelName');
      setRecordingError('Recording not available');
      return;
    }

    try {
      setRecordingStatus('starting');
      setRecordingError(null);
      
      const response = await authenticatedAxios.post('/video-call/start-recording', {
        channelName: channelName,
        conversationId: channelName, // channelName is the conversationId
        userId: uid
      });

      if (response.data.success) {
        setIsRecording(true);
        setRecordingId(response.data.data.recordingId);
        setRecordingStatus('recording');
        console.log('Recording started:', response.data.data.recordingId);
      } else {
        throw new Error(response.data.error || 'Failed to start recording');
      }
    } catch (err) {
      console.error('Error starting recording:', err);
      setRecordingStatus('failed');
      setRecordingError(err.response?.data?.error || err.message || 'Failed to start recording');
      setIsRecording(false);
    }
  };

  // Stop recording
  const stopRecording = async () => {
    if (!authenticatedAxios || !recordingId) {
      console.error('Cannot stop recording: missing authenticatedAxios or recordingId');
      return;
    }

    // Immediately stop animation and disable button
    setIsStoppingRecording(true);
    setIsRecording(false);

    try {
      setRecordingStatus('stopping');
      
      const response = await authenticatedAxios.post('/video-call/stop-recording', {
        recordingId: recordingId,
        conversationId: channelName
      });

      if (response.data.success) {
        setRecordingStatus('processing');
        console.log('Recording stopped:', response.data.data);
        
        // Show processing status briefly, then reset
        setTimeout(() => {
          setRecordingStatus('done');
          setTimeout(() => {
            setRecordingStatus('idle');
            setRecordingId(null);
            setIsStoppingRecording(false); // Re-enable button after full process
          }, 3000);
        }, 2000);
      } else {
        throw new Error(response.data.error || 'Failed to stop recording');
      }
    } catch (err) {
      console.error('Error stopping recording:', err);
      setRecordingStatus('failed');
      setRecordingError(err.response?.data?.error || err.message || 'Failed to stop recording');
      setIsStoppingRecording(false); // Re-enable on error
    }
  };

  // Toggle recording
  const toggleRecording = async () => {
    if (isRecording) {
      await stopRecording();
    } else {
      await startRecording();
    }
  };

  // Get recording status text
  const getRecordingStatusText = () => {
    switch (recordingStatus) {
      case 'starting': return 'Starting...';
      case 'recording': return 'Recording';
      case 'stopping': return 'Stopping...';
      case 'processing': return 'Processing...';
      case 'done': return 'Saved';
      case 'failed': return 'Failed';
      default: return '';
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // View mode icons
  const SplitViewIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1.5" y="2.5" width="5" height="11" rx="1" />
      <rect x="9.5" y="2.5" width="5" height="11" rx="1" />
    </svg>
  );

  const PictureInPictureIcon = () => (
    <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1" />
      <rect x="9" y="5.5" width="4.5" height="3" rx="0.5" fill="currentColor" stroke="none" />
    </svg>
  );

  const changeViewMode = (mode) => {
    setViewMode(mode);
  };



  if (error) {
    return (
      <VideoCallContainer>
        <ErrorMessage>
          {error}
          <br />
          <button onClick={() => window.location.reload()}>Retry</button>
        </ErrorMessage>
      </VideoCallContainer>
    );
  }

  return (
    <VideoCallContainer>
      <Header>
        <div style={{ minWidth: '120px' }}>
          {/* Recording indicator */}
          {(isRecording || recordingStatus === 'processing' || recordingStatus === 'done') && (
            <RecordingIndicator $isRecording={isRecording}>
              {isRecording && <RecordingDot />}
              <span>{getRecordingStatusText()}</span>
            </RecordingIndicator>
          )}
        </div>
        <CallInfo>
          <CallTitle>Call with {userName}</CallTitle>
          <CallStatus>
            {isConnecting ? 'Connecting...' : isConnected ? `Connected - ${formatDuration(callDuration)}` : 'Disconnected'}
          </CallStatus>
        </CallInfo>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <ViewModeControls>
            <ViewModeButton
              $active={viewMode === 'split'}
              onClick={() => changeViewMode('split')}
              title="Split View"
            >
              <SplitViewIcon />
            </ViewModeButton>
            <ViewModeButton
              $active={viewMode === 'pip'}
              onClick={() => changeViewMode('pip')}
              title="Picture in Picture"
            >
              <PictureInPictureIcon />
            </ViewModeButton>
          </ViewModeControls>
        </div>
      </Header>

      {/* Render based on view mode */}
      {viewMode === 'split' && (
        <SplitViewContainer>
          {/* Expert video first (left on desktop, top on mobile) */}
          {remoteUsers.length > 0 ? (
            remoteUsers.map(user => (
              <VideoContainer key={user.uid} style={{ flex: 1 }}>
                <VideoElement 
                  ref={el => remoteVideoRefs.current[user.uid] = el}
                  autoPlay 
                />
                <VideoLabel>{userName}</VideoLabel>
              </VideoContainer>
            ))
          ) : (
            <VideoContainer style={{ flex: 1 }}>
              <LoadingSpinner>
                {isConnecting ? 'Connecting...' : 'Waiting for participant to join...'}
              </LoadingSpinner>
            </VideoContainer>
          )}
          {/* User video second (right on desktop, bottom on mobile) */}
          <VideoContainer style={{ flex: 1 }}>
            <VideoElement ref={localVideoRef} autoPlay muted />
            <VideoLabel>You</VideoLabel>
          </VideoContainer>
        </SplitViewContainer>
      )}

      {/* Picture in picture view mode */}
      {viewMode === 'pip' && (
        <PictureInPictureContainer>
          {/* Main video (expert) */}
          {remoteUsers.length > 0 ? (
            remoteUsers.map(user => (
              <VideoContainer key={user.uid} style={{ width: '100%', height: '100%' }}>
                <VideoElement 
                  ref={el => remoteVideoRefs.current[user.uid] = el}
                  autoPlay 
                />
                <VideoLabel>{userName}</VideoLabel>
              </VideoContainer>
            ))
          ) : (
            <VideoContainer style={{ width: '100%', height: '100%' }}>
              <LoadingSpinner>
                {isConnecting ? 'Connecting...' : 'Waiting for participant to join...'}
              </LoadingSpinner>
            </VideoContainer>
          )}
          {/* Small overlay video (user) */}
          <SmallVideoContainer>
            <VideoElement ref={localVideoRef} autoPlay muted />
            <VideoLabel style={{ 
              fontSize: '10px', 
              padding: '4px 8px',
              bottom: '8px',
              left: '8px'
            }}>You</VideoLabel>
          </SmallVideoContainer>
        </PictureInPictureContainer>
      )}

      <Controls>
        <ControlButton 
          onClick={toggleAudio}
          active={isAudioEnabled}
          disabled={isTestMode}
          title={isAudioEnabled ? 'Mute' : 'Unmute'}
        >
          {isAudioEnabled ? <FaMicrophone /> : <FaMicrophoneSlash />}
        </ControlButton>
        <ControlButton 
          onClick={toggleVideo}
          active={isVideoEnabled}
          disabled={isTestMode}
          title={isVideoEnabled ? 'Turn off camera' : 'Turn on camera'}
        >
          {isVideoEnabled ? <FaVideo /> : <FaVideoSlash />}
        </ControlButton>
        
        {/* Recording button */}
        <RecordingButton
          onClick={toggleRecording}
          $isRecording={isRecording && !isStoppingRecording}
          disabled={isTestMode || !authenticatedAxios || recordingStatus === 'starting' || isStoppingRecording}
          title={isStoppingRecording ? 'Processing...' : (isRecording ? 'Stop Recording' : 'Start Recording')}
        >
          {isRecording && !isStoppingRecording ? <FaStop /> : <FaCircle />}
        </RecordingButton>
        
        <ControlButton 
          onClick={endCall}
          danger
          title="End Call"
        >
          <FaPhoneSlash />
        </ControlButton>
      </Controls>
    </VideoCallContainer>
  );
};

export default VideoCall;
